import React, { useEffect, useState } from 'react';
import {
  createFrontendModule,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  CatalogIndexPage,
  CatalogTableRow,
} from '@backstage/plugin-catalog/alpha';
import { TableColumn } from '@backstage/core-components';
import { Link } from 'react-router-dom';
import { Chip, Tooltip, Typography } from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';

// Live deployment images, sourced from ArgoCD (the deployed truth — template-2
// users build/push their own images so no create-time annotation exists).
// Keyed by the same selector string the ArgoCD Applications carry as labels:
//   caas.hkust/app=<name>[,caas.hkust/env=<env>]
const useArgocdImages = () => {
  const { fetch } = useApi(fetchApiRef);
  const [map, setMap] = useState<Record<string, string>>({});
  useEffect(() => {
    (window as any).__argocdFetch = { started: Date.now(), status: 'running' };
    fetch('/api/backstage-community-argocd/argoInstance/akslocal5/applications/selector/' + encodeURIComponent('caas.hkust/app'))
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: { items?: Array<{
        metadata: { labels?: Record<string, string> };
        status?: { summary?: { images?: string[] } };
      }> }) => {
        const m: Record<string, string> = {};
        for (const app of d.items ?? []) {
          const labels = app.metadata?.labels ?? {};
          if (!labels['caas.hkust/app']) continue;
          const key = `caas.hkust/app=${labels['caas.hkust/app']}` +
            (labels['caas.hkust/env'] ? `,caas.hkust/env=${labels['caas.hkust/env']}` : '');
          // KubeBlocks pods pull infra images too — drop them; keep the app's own
          const images = (app.status?.summary?.images ?? []).filter(img => !/kubeblocks|apecloud/i.test(img));
          if (images.length) m[key] = images.join(', ');
        }
        setMap(m);
        (window as any).__argocdFetch = { status: 'ok', keys: Object.keys(m), rawApps: (d.items ?? []).length };
      })
      .catch((e: Error) => {
        (window as any).__argocdFetch = { status: 'fail', error: String(e) };
      });
  }, [fetch]);
  return map;
};

// CaaS catalog table: platform-relevant columns instead of the generic
// defaults (System/Type carry no information for us). Data comes from the
// caas.hkust/* annotations written by the create workflow:
//   Name(+description) | Namespace | Cluster | Domain | Owner | Lifecycle | Image:tag
const ann = (e: Entity, key: string): string =>
  e.metadata.annotations?.[`caas.hkust/${key}`] ?? '—';

const lifecycleChip = (value: string) => {
  const color =
    value === 'production'
      ? 'primary'
      : value === 'retired'
        ? 'default'
        : value === 'deprecated'
          ? 'secondary'
          : 'default';
  const variant = value === 'experimental' ? 'outlined' : 'default';
  return <Chip label={value} size="small" color={color as never} variant={variant as never} />;
};

export const CustomCatalogPage = () => {
  const images = useArgocdImages();
  return <CatalogIndexPage columns={columnsFor(images)} />;
};

// columns need the live images map → build them via a factory
const columnsFor = (images: Record<string, string>): TableColumn<CatalogTableRow>[] => [
  {
    title: 'Name',
    field: 'entity.metadata.title',
    render: row => {
      const e = row.entity;
      const href = `/catalog/${e.metadata.namespace ?? 'default'}/${e.kind.toLowerCase()}/${e.metadata.name}`;
      return (
        <div style={{ minWidth: 220 }}>
          <Link to={href} style={{ textDecoration: 'none' }}>
            <Typography variant="body1" style={{ fontWeight: 600 }}>
              {e.metadata.title ?? e.metadata.name}
            </Typography>
          </Link>
          {e.metadata.description && (
            <Typography
              variant="body2"
              color="textSecondary"
              noWrap
              style={{ maxWidth: 320 }}
            >
              {e.metadata.description}
            </Typography>
          )}
        </div>
      );
    },
  },
  {
    title: 'Namespace',
    render: row => ann(row.entity, 'namespace'),
  },
  {
    title: 'Cluster',
    render: row => ann(row.entity, 'cluster'),
  },
  {
    title: 'Domain',
    render: row => {
      const domain = ann(row.entity, 'domain');
      if (domain === '—') return domain;
      // retired tombstones keep plain text (the site is gone)
      if (row.entity.spec?.lifecycle === 'retired') {
        return <span style={{ color: 'rgba(0,0,0,0.38)' }}>{domain}</span>;
      }
      return (
        <a
          href={`https://${domain}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          {domain}
        </a>
      );
    },
  },
  {
    title: 'Owner',
    field: 'entity.spec.owner',
    render: row => row.entity.spec?.owner ?? '—',
  },
  {
    title: 'Lifecycle',
    field: 'entity.spec.lifecycle',
    render: row => lifecycleChip(row.entity.spec?.lifecycle ?? '—'),
  },
  {
    title: 'Image',
    render: row => {
      // template-2 apps have no create-time image annotation — read the
      // DEPLOYED truth from ArgoCD (keyed by their per-env selector)
      const e = row.entity;
      const selector = e.metadata.annotations?.['argocd/app-selector'];
      const list = (selector && images[selector]) || '';
      const pick = (l: string): string => {
        const imgs = l.split(', ');
        // our ACR hosts the app's own CI-built image; infra jobs (kubectl/
        // mysql/postgres/apecloud) also appear — prefer the app's own image
        return (
          imgs.find(i => i.startsWith('acraksys64gn2f5izuw.azurecr.io/')) ||
          imgs.find(i => !/(kubeblocks|apecloud|bitnami\/kubectl|mysql:|postgres)/i.test(i)) ||
          l
        );
      };
      const image = selector && list ? pick(list) : ann(e, 'image');
      if (image === '—') return image;
      const full = selector && list ? list : image;
      return (
        <Tooltip title={full} arrow>
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 12,
              display: 'inline-block',
              maxWidth: 240,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle',
            }}
          >
            {image}
          </span>
        </Tooltip>
      );
    },
  },
];

export const customCatalogTableModule = createFrontendModule({
  // pluginId must be the TARGET plugin's id for the page override to take
  // precedence over the catalog plugin's own /catalog page.
  pluginId: 'catalog',
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/catalog',
        // The sidebar builds its items from page extensions' titles
        // (nav.take('page:catalog')) — without a title the link vanishes.
        title: 'Catalog',
        loader: async () => <CustomCatalogPage />,
      },
    }),
  ],
});
